import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, Lock, Sparkles, ChevronDown, ChevronUp,
  Target, Brain, BookOpen, Calculator, MessageSquare, TrendingUp,
  Shield, ScanLine, BarChart2, Activity, History, Zap, FlaskConical,
  Trophy, Star, ArrowRight, Map, PieChart,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import PlaybookDecisionEngine from '../components/PlaybookDecisionEngine';
import OnboardingMap from '../components/OnboardingMap';
import TradeJourneyMap from '../components/TradeJourneyMap';
import PlaybookMap from '../components/PlaybookMap';
import AppIndexMap from '../components/AppIndexMap';
import TradePlanCards from '../components/TradePlanCards';

// ─── Level colours ────────────────────────────────────────────────────────────
const LV_COLOR = {
  1: '#6c63ff',
  2: '#4ecca3',
  3: '#f97316',
  4: '#ce93d8',
};

// ─── Rich task metadata for all 4 levels ─────────────────────────────────────
const TASK_META = {
  risk_profile: {
    icon: Shield,
    title: 'Complete Your Risk Profile',
    why: 'Knowing your risk tolerance prevents oversizing trades and blowing up your account.',
    time: '5 min',
    path: '/profile?section=risk-profile',
    cta: 'Start Risk Profile',
  },
  learning_day1: {
    icon: BookOpen,
    title: 'ACT Day 1 — Trade Fundamentals',
    why: 'You need to know what Stop Loss and R:R ratio mean before placing a single order.',
    time: '10 min',
    path: '/learning',
    cta: 'Start Learning',
  },
  risk_calc: {
    icon: Calculator,
    title: 'Try the Risk Calculator',
    why: 'Position sizing by gut feel is how traders blow up. Math fixes this.',
    time: '3 min',
    path: '/calculator',
    cta: 'Open Calculator',
  },
  nyx_intro: {
    icon: MessageSquare,
    title: 'Ask Nyx Your First Question',
    why: 'Your AI coach knows your data, your mistakes, and your blind spots. Start using it.',
    time: '2 min',
    path: '/ai-coach',
    cta: 'Talk to Nyx',
    premium: true,
  },
  first_trade: {
    icon: TrendingUp,
    title: 'Place Your First Paper Trade',
    why: 'Every trade needs 3 things: Stop Loss, Target, and a written reason. Start the habit now.',
    time: '5 min',
    path: '/trade',
    cta: 'Place First Trade',
  },
  trade_with_sl: {
    icon: Target,
    title: 'Trade With a Stop Loss',
    why: 'Trades without SL are gambles. Prove you can execute a disciplined entry.',
    time: '5 min',
    path: '/trade',
    cta: 'Place Trade',
  },
  scanners: {
    icon: ScanLine,
    title: 'Explore the Scanners',
    why: 'Scanners surface setups you would never find manually. This is your stock radar.',
    time: '5 min',
    path: '/analytics',
    cta: 'Open Scanners',
  },
  stock_chart: {
    icon: BarChart2,
    title: 'Read a Stock Chart',
    why: 'Price tells a story. Learning to read it removes guesswork from your decisions.',
    time: '5 min',
    path: '/markets',
    cta: 'Open Markets',
  },
  indicators: {
    icon: Activity,
    title: 'Use an Indicator',
    why: 'One well-understood indicator beats ten you don\'t understand.',
    time: '5 min',
    path: '/ai-coach',
    cta: 'Ask Nyx',
  },
  orders_page: {
    icon: Zap,
    title: 'Explore the Orders Page',
    why: 'Know the difference between market, limit, and stop orders before you need them.',
    time: '5 min',
    path: '/orders',
    cta: 'Open Orders',
  },
  trade_history: {
    icon: History,
    title: 'Review Your Trade History',
    why: 'Your past trades are your best teacher. Most traders never look back.',
    time: '10 min',
    path: '/trade-history',
    cta: 'Open Trade History',
  },
  discipline_page: {
    icon: Shield,
    title: 'Check Your Discipline Score',
    why: 'Discipline is measurable. See exactly where you break your own rules.',
    time: '5 min',
    path: '/discipline',
    cta: 'Open Discipline',
  },
  analytics: {
    icon: PieChart,
    title: 'Explore Trade Analytics',
    why: 'Win rate alone lies. Your analytics show the full picture: expectancy, drawdown, streaks.',
    time: '5 min',
    path: '/trading-analytics',
    cta: 'Open Analytics',
    premium: true,
  },
  journal: {
    icon: BookOpen,
    title: 'Write in Your Trade Journal',
    why: 'Traders who journal improve 40% faster. Writing forces clarity.',
    time: '5 min',
    path: '/trade',
    cta: 'Open Journal',
  },
  nyx_review: {
    icon: Brain,
    title: 'Get Nyx\'s Behavioral Review',
    why: 'Nyx has read every trade you\'ve made. Ask it what patterns it sees.',
    time: '5 min',
    path: '/ai-coach',
    cta: 'Ask Nyx',
    premium: true,
  },
  backtest: {
    icon: FlaskConical,
    title: 'Run Your First Backtest',
    why: 'Feelings about a strategy are worthless. Data isn\'t.',
    time: '10 min',
    path: '/backtest',
    cta: 'Open Backtest',
    premium: true,
  },
  algo_flow: {
    icon: Zap,
    title: 'Build an Algo Flow',
    why: 'Remove emotion from entries. Code your rules, let them execute.',
    time: '10 min',
    path: '/algo-flows',
    cta: 'Open Algo Flows',
    premium: true,
  },
  analysis_mode: {
    icon: Brain,
    title: 'Deep Analysis with Nyx',
    why: 'Nyx can analyse your entire strategy, not just individual trades.',
    time: '10 min',
    path: '/ai-coach',
    cta: 'Open Nyx',
    premium: true,
  },
  trade_report: {
    icon: Map,
    title: 'Generate a Trade Report',
    why: 'A monthly report holds you accountable and reveals if your edge is real.',
    time: '5 min',
    path: '/profile',
    cta: 'Open Report',
  },
};

const LV_META = [
  { label: 'Know Yourself',    subtitle: 'Before you trade a rupee, know who you are as a trader.' },
  { label: 'Foundation',       subtitle: 'Build the habits and skills that every profitable trader has.' },
  { label: 'Behavioral Audit', subtitle: 'Look at your real data — honestly. Find your blind spots.' },
  { label: 'Build a System',   subtitle: 'Turn what works into a repeatable, rule-based edge.' },
];

// ─── Daily Trainer sessions (rotates daily) ───────────────────────────────────
const TRAINER_SESSIONS = [
  {
    topic: 'Stop Loss First',
    concept: 'A Stop Loss is not optional. Set it before you enter, not after. If you don\'t know where you\'re wrong, you\'re not trading — you\'re guessing.',
    challenge: 'Review your last 3 trades. Did every one have a Stop Loss set at entry?',
    action: { label: 'Open Trade History', path: '/trade-history' },
    color: '#6c63ff',
    icon: Shield,
  },
  {
    topic: 'The 1% Rule',
    concept: 'Never risk more than 1–2% of your total capital on a single trade. 10 consecutive losses should still leave you in the game.',
    challenge: 'Open the Risk Calculator. Enter ₹500 SL on ₹50,000 capital. See exactly how many shares to buy.',
    action: { label: 'Risk Calculator', path: '/trade' },
    color: '#4ecca3',
    icon: Calculator,
  },
  {
    topic: 'Reward-to-Risk Ratio',
    concept: 'For every rupee you risk, aim to make at least 2. A 1:2 R:R means even a 40% win rate is profitable over time.',
    challenge: 'Look at your last 5 trades. Calculate the actual R:R achieved on each one.',
    action: { label: 'Trade History', path: '/trade-history' },
    color: '#f97316',
    icon: Target,
  },
  {
    topic: 'Trade With a Reason',
    concept: 'Before clicking Buy, write one sentence: "I am buying because ___." No reason = no trade. This habit alone separates traders from gamblers.',
    challenge: 'Place a paper trade today and write the reason in the notes field. Don\'t skip it.',
    action: { label: 'Place a Trade', path: '/trade' },
    color: '#6c63ff',
    icon: Brain,
  },
  {
    topic: 'Pre-Market Routine',
    concept: 'The 30 minutes before 9:15 matter more than the 6 hours after. Check global cues, gap-ups/downs, and your watchlist before market opens.',
    challenge: 'Open Global Cues right now. Read the pre-market data. What is SGX Nifty indicating?',
    action: { label: 'Global Cues', path: '/global-cues' },
    color: '#4ecca3',
    icon: TrendingUp,
  },
  {
    topic: 'Your Discipline Score',
    concept: 'Discipline is measurable. Your score shows exactly which rules you break — trades held too long, cut too early, or entered without reason.',
    challenge: 'Open the Discipline page. Find the one rule you break most often. Name it.',
    action: { label: 'Discipline Score', path: '/discipline' },
    color: '#ce93d8',
    icon: Shield,
  },
  {
    topic: 'Win Rate Is a Lie',
    concept: 'A 60% win rate means nothing if your average loss is 3× your average win. What matters is expectancy: (Win% × Avg Win) − (Loss% × Avg Loss).',
    challenge: 'Open Analytics. Find your average win and average loss in rupees. Calculate your expectancy.',
    action: { label: 'Trading Analytics', path: '/analytics' },
    color: '#6c63ff',
    icon: PieChart,
  },
  {
    topic: 'Plan the Exit Before Entry',
    concept: 'Most traders obsess over entries. The exit determines your profit. Know before you enter: "If price hits X, I book profit. If price hits Y, I exit at SL."',
    challenge: 'Review your last 3 winning trades. Did you exit at your original target, or did you exit early?',
    action: { label: 'Trade History', path: '/trade-history' },
    color: '#4ecca3',
    icon: Activity,
  },
  {
    topic: 'Scan Before You Pick',
    concept: 'Don\'t scroll through hundreds of stocks manually. Scanners surface setups matching your criteria automatically. This is your stock radar.',
    challenge: 'Open Scanners today. Use one scanner and find 3 stocks that appear in results.',
    action: { label: 'Open Scanners', path: '/analytics' },
    color: '#ce93d8',
    icon: ScanLine,
  },
];

// ─── Feature Map data ─────────────────────────────────────────────────────────
const FEATURE_PHASES = [
  {
    phase: 'Before Trading',
    color: '#6c63ff',
    desc: 'Do these before 9:15 AM every trading day.',
    features: [
      {
        icon: TrendingUp,
        name: 'Global Cues',
        path: '/global-cues',
        what: 'Pre-market snapshot — SGX Nifty, US futures, FII/DII flows, and key gap-up/gap-down stocks.',
        when: 'Every morning before 9:15 AM. Takes 5 minutes. Skip it and you\'re trading blind.',
      },
      {
        icon: ScanLine,
        name: 'Scanners',
        path: '/analytics',
        what: 'Automatically surfaces stocks matching technical criteria — breakouts, momentum, volume spikes.',
        when: 'Before market open to build your watchlist. Don\'t pick stocks randomly.',
      },
      {
        icon: Calculator,
        name: 'Risk Calculator',
        path: '/calculator',
        what: 'Enter your SL in rupees → get the exact number of shares to buy. Never oversize a trade again.',
        when: 'Before every single trade you place. Not optional.',
      },
      {
        icon: BookOpen,
        name: 'ACT Learning',
        path: '/learning',
        what: 'Structured trading education — concepts, scenarios, and quizzes built for your current level.',
        when: '10 minutes daily. Compound knowledge like you compound returns.',
      },
    ],
  },
  {
    phase: 'During Trading',
    color: '#4ecca3',
    desc: 'Tools for market hours — 9:15 AM to 3:30 PM.',
    features: [
      {
        icon: BarChart2,
        name: 'Markets & Charts',
        path: '/markets',
        what: 'Price charts with overlays for stocks and F&O. Read the setup before entering.',
        when: 'Before entering a trade. Also to track open positions throughout the day.',
      },
      {
        icon: TrendingUp,
        name: 'Place a Trade',
        path: '/trade',
        what: 'Paper trade with Stop Loss, Target, and a written reason. The reason field is not optional.',
        when: 'Only when you have a clear setup and a clear reason. No reason — no trade.',
      },
      {
        icon: MessageSquare,
        name: 'Nyx — AI Coach',
        path: '/ai-coach',
        what: 'Your AI coach who has read every trade you\'ve made. Validates setups, explains stocks, spots behavioral patterns.',
        when: 'Before a trade to validate the idea. After a loss to understand what went wrong.',
      },
      {
        icon: Zap,
        name: 'Orders & Positions',
        path: '/orders',
        what: 'View all open positions. Modify SL, book partial profits, or exit a trade.',
        when: 'While you have open positions. Check it — don\'t stare at it all day.',
      },
      {
        icon: Activity,
        name: 'Derivatives (F&O)',
        path: '/derivatives',
        what: 'Options chain, Futures data, and OI analysis for Nifty, Banknifty, and stocks.',
        when: 'Before trading options or futures. Know the OI buildup and PCR before entering.',
      },
    ],
  },
  {
    phase: 'After Trading',
    color: '#f97316',
    desc: 'Your real learning happens here — after 3:30 PM.',
    features: [
      {
        icon: History,
        name: 'Trade History',
        path: '/trade-history',
        what: 'Complete log of every trade — P&L, SL adherence, and the reason you wrote at entry.',
        when: 'Every evening. Read what you wrote, see what actually happened. Honest review.',
      },
      {
        icon: Shield,
        name: 'Discipline Score',
        path: '/discipline',
        what: 'A 0–100 score measuring how consistently you follow your own rules. Pinpoints exactly which rules you break.',
        when: 'Weekly check. Find your #1 discipline leak and fix it.',
      },
      {
        icon: PieChart,
        name: 'Trading Analytics',
        path: '/trading-analytics',
        what: 'Win rate, expectancy, average win/loss, streaks, and drawdown. Tells you if your edge is real or imagined.',
        when: 'Weekly review. Numbers don\'t lie — feelings do.',
      },
      {
        icon: Map,
        name: 'Performance Report',
        path: '/profile',
        what: 'Monthly summary of your trading performance, capital usage, and improvement areas.',
        when: 'End of month. Hold yourself accountable to the data.',
      },
    ],
  },
  {
    phase: 'Build a System',
    color: '#ce93d8',
    desc: 'Advanced tools — use once you\'re consistently profitable manually.',
    features: [
      {
        icon: FlaskConical,
        name: 'Backtest',
        path: '/backtest',
        what: 'Test any strategy on historical market data. See how it would have performed over months before risking a rupee.',
        when: 'Before adopting any new strategy. Feelings are worthless. Data isn\'t.',
      },
      {
        icon: Zap,
        name: 'Algo Flows',
        path: '/algo-flows',
        what: 'Build rule-based trading strategies. Define entry and exit conditions — remove emotion from execution.',
        when: 'Once you have a profitable manual system. Code what works, let it run consistently.',
      },
    ],
  },
];

// ─── Ring SVG ─────────────────────────────────────────────────────────────────
function Ring({ pct, size = 60, stroke = 5, color }) {
  const r    = (size - stroke * 2) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} strokeWidth={stroke} fill="none" stroke={`${color}20`} />
        <circle cx={size/2} cy={size/2} r={r} strokeWidth={stroke} fill="none"
          style={{ stroke: color, strokeDasharray: `${(pct/100)*circ} ${circ}`, strokeLinecap: 'round', transition: 'stroke-dasharray 1s ease' }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span style={{ fontSize: size * 0.22, fontWeight: 800, color, lineHeight: 1 }}>{pct}%</span>
      </div>
    </div>
  );
}

// ─── Daily Trainer Card ───────────────────────────────────────────────────────
function DailyTrainerCard({ colors, navigate }) {
  const dayOfYear = Math.floor(
    (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000
  );
  const session    = TRAINER_SESSIONS[dayOfYear % TRAINER_SESSIONS.length];
  const sessionNum = (dayOfYear % TRAINER_SESSIONS.length) + 1;
  const Icon       = session.icon;

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ border: `1px solid ${session.color}35` }}>

      {/* Coloured header bar */}
      <div className="flex items-center gap-2 px-4 py-2.5"
        style={{ background: `linear-gradient(135deg, ${session.color}28 0%, ${session.color}10 100%)` }}>
        <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${session.color} 0%, ${session.color}bb 100%)` }}>
          <Brain className="w-3.5 h-3.5 text-white" />
        </div>
        <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: session.color }}>
          Daily Trainer
        </span>
        <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: `${session.color}18`, color: session.color }}>
          Session {sessionNum}/{TRAINER_SESSIONS.length}
        </span>
      </div>

      {/* Body */}
      <div className="p-4" style={{ background: colors.cardBg }}>
        {/* Topic */}
        <div className="flex items-center gap-2.5 mb-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${session.color}15`, border: `1px solid ${session.color}30` }}>
            <Icon className="w-4 h-4" style={{ color: session.color }} />
          </div>
          <p className="text-[15px] font-black leading-tight" style={{ color: colors.primaryText }}>
            {session.topic}
          </p>
        </div>

        {/* Concept */}
        <p className="text-[12px] leading-relaxed mb-3" style={{ color: colors.secondaryText }}>
          {session.concept}
        </p>

        {/* Challenge */}
        <div className="rounded-xl p-3 mb-3"
          style={{ background: `${session.color}10`, border: `1px solid ${session.color}28` }}>
          <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: session.color }}>
            Your Challenge Today
          </p>
          <p className="text-[12px] leading-relaxed" style={{ color: colors.primaryText }}>
            {session.challenge}
          </p>
        </div>

        {/* CTA */}
        <button onClick={() => navigate(session.action.path)}
          className="flex items-center justify-center gap-1.5 w-full py-2.5 rounded-xl text-[12px] font-bold"
          style={{ background: `linear-gradient(135deg, ${session.color} 0%, ${session.color}cc 100%)`, color: '#fff' }}>
          {session.action.label} <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Single rich task card ────────────────────────────────────────────────────
function TaskCard({ taskId, done, isNext, locked, lvColor, colors, onNavigate, meetsMinPlan }) {
  const meta = TASK_META[taskId];
  if (!meta) return null;
  const Icon = meta.icon;
  const premiumLocked = meta.premium && !meetsMinPlan('premium');

  return (
    <div className="rounded-xl transition-all"
      style={{
        background: done
          ? `linear-gradient(135deg, ${lvColor}12 0%, ${lvColor}05 100%)`
          : isNext
          ? `linear-gradient(135deg, ${lvColor}18 0%, ${lvColor}08 100%)`
          : `linear-gradient(135deg, ${colors.cardBg}80 0%, ${colors.cardBg}40 100%)`,
        border: done
          ? `1px solid ${lvColor}35`
          : isNext
          ? `1px solid ${lvColor}50`
          : `1px solid ${colors.border}`,
        opacity: locked ? 0.45 : 1,
      }}>
      <div className="flex items-center gap-3 p-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: done || isNext ? `${lvColor}20` : `${colors.border}30`, border: `1px solid ${done || isNext ? lvColor + '35' : colors.border}` }}>
          {done
            ? <CheckCircle className="w-4 h-4" style={{ color: lvColor }} />
            : locked
            ? <Lock className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
            : <Icon className="w-4 h-4" style={{ color: isNext ? lvColor : colors.secondaryText }} />}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold leading-tight"
            style={{ color: done ? lvColor : isNext ? colors.primaryText : colors.secondaryText }}>
            {meta.title}
          </p>
          {(isNext || done) && (
            <p className="text-[11px] mt-0.5 leading-relaxed" style={{ color: colors.secondaryText }}>
              {meta.why}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          {done && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: `${lvColor}20`, color: lvColor }}>
              Done
            </span>
          )}
          {premiumLocked && (
            <button onClick={() => navigate('/profile?section=subscription')}
              className="flex items-center gap-1 px-2 py-1 rounded-xl text-[11px] font-bold"
              style={{ background: 'rgba(234,179,8,0.15)', color: '#eab308', border: '1px solid rgba(234,179,8,0.3)' }}>
              <Lock className="w-2.5 h-2.5" /> Premium
            </button>
          )}
          {isNext && !done && !locked && !premiumLocked && (
            <button onClick={onNavigate}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-[12px] font-bold"
              style={{ background: `linear-gradient(135deg, ${lvColor} 0%, ${lvColor}bb 100%)`, color: '#fff' }}>
              {meta.cta} <ArrowRight className="w-3 h-3" />
            </button>
          )}
          {!done && !isNext && !locked && !premiumLocked && (
            <span className="text-[11px] px-2 py-0.5 rounded-full"
              style={{ background: `${colors.border}50`, color: colors.secondaryText }}>
              {meta.time}
            </span>
          )}
          {locked && !premiumLocked && <Lock className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />}
        </div>
      </div>
    </div>
  );
}

// ─── Hero "Your Next Move" card ───────────────────────────────────────────────
function NextMoveCard({ taskId, lvColor, lvLabel, colors, onNavigate }) {
  const meta = TASK_META[taskId];
  if (!meta) return null;
  const Icon = meta.icon;

  return (
    <div className="rounded-2xl p-4"
      style={{
        background: `linear-gradient(135deg, ${lvColor}22 0%, ${lvColor}0a 100%)`,
        border: `1px solid ${lvColor}45`,
      }}>
      <div className="flex items-center gap-1.5 mb-3">
        <div className="w-5 h-5 rounded-lg flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${lvColor} 0%, ${lvColor}bb 100%)` }}>
          <Sparkles className="w-3 h-3 text-white" />
        </div>
        <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: lvColor }}>
          Your Next Move
        </span>
        <span className="ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: `${lvColor}18`, color: lvColor }}>
          {lvLabel}
        </span>
      </div>

      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${lvColor}30 0%, ${lvColor}10 100%)`, border: `1px solid ${lvColor}40` }}>
          <Icon className="w-5 h-5" style={{ color: lvColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-bold leading-snug mb-1" style={{ color: colors.primaryText }}>
            {meta.title}
          </p>
          <p className="text-[12px] leading-relaxed" style={{ color: colors.secondaryText }}>
            {meta.why}
          </p>
        </div>
      </div>

      <button onClick={onNavigate}
        className="mt-3 w-full py-2.5 rounded-xl text-[13px] font-bold flex items-center justify-center gap-2"
        style={{ background: `linear-gradient(135deg, ${lvColor} 0%, ${lvColor}cc 100%)`, color: '#fff' }}>
        {meta.cta} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Chapter / Level card (collapsible) ──────────────────────────────────────
function LevelCard({ level, meta, lvColor, tasks, doneIds, isCurrent, isLocked, colors, navigate }) {
  const [open, setOpen] = useState(isCurrent);
  const doneCount  = tasks.filter(t => doneIds.has(t.id)).length;
  const total      = tasks.length;
  const pct        = Math.round((doneCount / total) * 100);
  const complete   = doneCount === total;
  const firstPendingIdx = tasks.findIndex(t => !doneIds.has(t.id));

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${lvColor}10 0%, ${lvColor}04 100%)`,
        border: `1px solid ${isCurrent ? lvColor + '40' : lvColor + '20'}`,
        opacity: isLocked ? 0.55 : 1,
      }}>

      <button
        className="w-full flex items-center gap-3 p-4 text-left"
        onClick={() => !isLocked && setOpen(o => !o)}
        disabled={isLocked}>

        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: complete ? `${lvColor}25` : isLocked ? `${colors.border}30` : `${lvColor}18`, border: `1px solid ${complete ? lvColor + '45' : lvColor + '25'}` }}>
          {complete
            ? <Trophy className="w-5 h-5" style={{ color: lvColor }} />
            : isLocked
            ? <Lock className="w-4 h-4" style={{ color: colors.secondaryText }} />
            : <Star className="w-5 h-5" style={{ color: lvColor }} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: lvColor }}>
              Level {level}
            </span>
            {complete && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: `${lvColor}20`, color: lvColor }}>
                Complete
              </span>
            )}
            {isCurrent && !complete && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse"
                style={{ background: `${lvColor}25`, color: lvColor }}>
                Active
              </span>
            )}
            {isLocked && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: `${colors.border}40`, color: colors.secondaryText }}>
                Locked
              </span>
            )}
          </div>
          <p className="text-[14px] font-bold leading-tight" style={{ color: colors.primaryText }}>
            {meta.label}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {!isLocked && (
            <div className="text-right">
              <span className="text-[13px] font-bold" style={{ color: lvColor }}>{doneCount}/{total}</span>
              <div className="w-12 h-1 rounded-full mt-1 overflow-hidden" style={{ background: `${lvColor}20` }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: lvColor }} />
              </div>
            </div>
          )}
          {!isLocked && (open
            ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: colors.secondaryText }} />
            : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: colors.secondaryText }} />)}
        </div>
      </button>

      {!isLocked && (
        <div className="px-4 pb-2 -mt-1">
          <p className="text-[11px] leading-relaxed" style={{ color: colors.secondaryText }}>{meta.subtitle}</p>
        </div>
      )}

      {open && !isLocked && (
        <div className="px-3 pb-3 space-y-2">
          {tasks.map((task, i) => {
            const done   = doneIds.has(task.id);
            const isNext = !done && i === firstPendingIdx;
            const locked = !done && i > firstPendingIdx + 1;
            const m      = TASK_META[task.id];
            return (
              <TaskCard
                key={task.id}
                taskId={task.id}
                done={done}
                isNext={isNext}
                locked={locked}
                lvColor={lvColor}
                colors={colors}
                onNavigate={() => m && navigate(m.path)}
              />
            );
          })}
        </div>
      )}

      {isLocked && (
        <div className="px-4 pb-4">
          <p className="text-[11px]" style={{ color: colors.secondaryText }}>
            Complete the previous level to unlock this chapter.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Feature Map section ──────────────────────────────────────────────────────
function FeatureMap({ colors, navigate }) {
  const [activePhase, setActivePhase] = useState(0);
  const [expanded,    setExpanded]    = useState(null);

  const phase = FEATURE_PHASES[activePhase];

  const handlePhase = (i) => { setActivePhase(i); setExpanded(null); };

  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${colors.border}` }}>

      {/* Card header */}
      <div className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: colors.cardHeaderBg, borderBottom: `1px solid ${colors.cardHeaderBorder}` }}>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #6c63ff 0%, #4ecca3 100%)' }}>
            <Map className="w-3 h-3 text-white" />
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: colors.secondaryText }}>
            App Guide
          </span>
        </div>
        <span className="text-[10px]" style={{ color: colors.secondaryText }}>
          {FEATURE_PHASES.reduce((n, p) => n + p.features.length, 0)} features
        </span>
      </div>

      {/* Phase tabs — scrollable pill row */}
      <div className="flex gap-1.5 px-3 py-2.5 overflow-x-auto"
        style={{ background: colors.cardHeaderBg, borderBottom: `1px solid ${colors.cardHeaderBorder}` }}>
        {FEATURE_PHASES.map(({ phase: label, color }, i) => (
          <button key={label}
            onClick={() => handlePhase(i)}
            className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all"
            style={activePhase === i
              ? { background: color, color: '#fff' }
              : { background: `${color}14`, color, border: `1px solid ${color}30` }}>
            {label}
          </button>
        ))}
      </div>

      {/* Phase description */}
      <div className="px-4 py-2" style={{ background: `${phase.color}08`, borderBottom: `1px solid ${phase.color}18` }}>
        <p className="text-[11px]" style={{ color: colors.secondaryText }}>{phase.desc}</p>
      </div>

      {/* Feature rows */}
      <div style={{ background: colors.cardBg }}>
        {phase.features.map((f, i) => {
          const Icon  = f.icon;
          const open  = expanded === i;
          const isLast = i === phase.features.length - 1;
          return (
            <div key={f.name}>
              <button
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all"
                onClick={() => setExpanded(open ? null : i)}
                style={{
                  background: open ? `${phase.color}0a` : 'transparent',
                  borderBottom: isLast && !open ? 'none' : `1px solid ${colors.border}`,
                }}>
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${phase.color}15`, border: `1px solid ${phase.color}28` }}>
                  <Icon className="w-4 h-4" style={{ color: phase.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold leading-tight" style={{ color: colors.primaryText }}>
                    {f.name}
                  </p>
                  {!open && (
                    <p className="text-[11px] mt-0.5 truncate" style={{ color: colors.secondaryText }}>
                      {f.when}
                    </p>
                  )}
                </div>
                {open
                  ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: phase.color }} />
                  : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: colors.secondaryText }} />}
              </button>

              {open && (
                <div className="px-4 pb-4 pt-0 de-accordion-enter"
                  style={{ background: `${phase.color}06`, borderBottom: isLast ? 'none' : `1px solid ${colors.border}` }}>
                  <div className="rounded-xl p-3 mb-3 space-y-2.5"
                    style={{ background: colors.cardBg, border: `1px solid ${phase.color}20` }}>
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: phase.color }}>
                        What it is
                      </p>
                      <p className="text-[12px] leading-relaxed" style={{ color: colors.secondaryText }}>
                        {f.what}
                      </p>
                    </div>
                    <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 8 }}>
                      <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: phase.color }}>
                        When to use it
                      </p>
                      <p className="text-[12px] leading-relaxed font-semibold" style={{ color: colors.primaryText }}>
                        {f.when}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => navigate(f.path)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[12px] font-bold"
                    style={{ background: phase.color, color: '#fff' }}>
                    Open {f.name} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── "One trade, start to finish" narrative (7 moments) ─────────────────────
const TRADE_STORY = [
  {
    time: '8:45 AM',
    phase: 'Pre-Market',
    color: '#6c63ff',
    icon: TrendingUp,
    title: 'The edge is built before market opens',
    situation: 'SGX Nifty is up 0.3%. US markets closed green. FII net bought ₹800 Cr yesterday. IT sector showing strength in pre-market futures.',
    move: 'You open Global Cues. Mild gap-up, macro is positive. You decide: today is a buy-side day. No stock picked yet — just context locked in.',
    lesson: 'Traders who skip this are trading blind. 5 minutes here decides your entire day\'s bias.',
    cta: 'Open Global Cues',
    path: '/global-cues',
  },
  {
    time: '9:05 AM',
    phase: 'Pre-Market',
    color: '#4ecca3',
    icon: ScanLine,
    title: 'The stock finds you — you don\'t hunt it',
    situation: 'Scanner hits: INFY approaching a 6-month resistance level at ₹1,820. Volume is building at 2.8× average before market even opens.',
    move: 'You mark ₹1,820 on the chart. You set a price alert. Your watchlist is now 1 stock deep — not 20. One clean setup. That\'s it.',
    lesson: 'Most traders have 20 stocks on a watchlist and trade none well. One focused setup beats ten half-baked ones every time.',
    cta: 'Open Scanners',
    path: '/analytics',
  },
  {
    time: '9:30 AM',
    phase: 'Market Open',
    color: '#f59e0b',
    icon: Activity,
    title: 'The open is the trap — patience is the edge',
    situation: 'Nifty gaps up 0.2%. INFY opens at ₹1,812, moving toward your ₹1,820 level. Everything looks bullish. The temptation to enter is real.',
    move: 'You do nothing. You watch the first 15 minutes play out from the sidelines. While you wait, you run through the Playbook — locking in today\'s global context, gap type, and open behavior before a single order is placed.',
    lesson: 'The first 15 minutes are the noisiest of the day. Smart money tests both sides before committing. Patience here costs nothing.',
    cta: 'Open Markets',
    path: '/markets',
    altCta: 'Run Playbook',
    altPath: '/appguide?tab=journey',
  },
  {
    time: '9:48 AM',
    phase: 'Setup Forms',
    color: '#4ecca3',
    icon: BarChart2,
    title: 'Breakout entry is risky — retest entry is disciplined',
    situation: 'INFY breaks ₹1,820 with a strong green candle. Volume spikes to 4× average. Your price alert fires.',
    move: 'You don\'t chase the breakout candle. In the Playbook you mark this as a Breakout Setup and confirm the instrument. You wait for the retest — price pulling back to ₹1,820 to confirm it has flipped from resistance to support.',
    lesson: 'The breakout candle traps impatient buyers. The retest separates the real move from the fake one.',
    cta: 'Open Charts',
    path: '/markets',
    altCta: 'Identify Setup in Playbook',
    altPath: '/appguide?tab=journey',
  },
  {
    time: '10:05 AM',
    phase: 'Entry',
    color: '#f97316',
    icon: Calculator,
    title: 'Position size is math, not feel',
    situation: 'INFY pulls back to ₹1,820, holds with a bullish rejection candle. The retest is confirmed. Playbook grade: B. Entry signal is clean.',
    move: 'Risk Calculator: ₹50,000 capital. SL at ₹1,793 (₹30 below entry). Risk 1% = ₹500 ÷ ₹30 = 16 shares. You buy 16 INFY @ ₹1,823. Reason: "Retest of 6-month breakout, 4× volume, bullish rejection candle."',
    lesson: 'The reason field is not a formality. If you can\'t write it in one sentence, the trade isn\'t ready.',
    cta: 'Risk Calculator',
    path: '/calculator',
    altCta: 'Check Playbook Grade',
    altPath: '/appguide?tab=journey',
  },
  {
    time: '12:20 PM',
    phase: 'Exit',
    color: '#16C784',
    icon: TrendingUp,
    title: 'Planned exit, executed — not hoped for',
    situation: 'INFY reaches ₹1,882. Your target was ₹1,883. The trade has run almost exactly as planned. Your P&L is +₹928.',
    move: 'You exit fully at ₹1,881. Position closed. You don\'t hold hoping for ₹1,920. The plan said ₹1,883 and you honour it.',
    lesson: 'Exiting at plan is a skill. Most traders hold winners too long chasing an extra ₹50 — and give back ₹300 when it reverses.',
    cta: 'View Orders',
    path: '/orders',
  },
  {
    time: '4:00 PM',
    phase: 'Review',
    color: '#ce93d8',
    icon: Shield,
    title: 'The review closes the loop — this is how habits form',
    situation: 'Market closed. P&L is green. Most traders have already forgotten the trade and moved on to tomorrow.',
    move: 'You open Trade History. You re-read the reason you wrote at entry. You check your Discipline Score. You write one line: "Waited for the retest. Sized by math. Exited at plan. Repeat."',
    lesson: 'One reviewed trade teaches more than ten ignored ones. The review is where a good trade becomes a repeatable system.',
    cta: 'Trade History',
    path: '/trade-history',
  },
];

// ─── "One trade, start to finish" narrative card ──────────────────────────────
function DayStepperCard({ colors, navigate }) {
  const [step, setStep] = useState(0);
  const s    = TRADE_STORY[step];
  const Icon = s.icon;
  const pct  = Math.round(((step + 1) / TRADE_STORY.length) * 100);

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ border: `1px solid ${s.color}35` }}>

      {/* Header */}
      <div className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: colors.cardHeaderBg, borderBottom: `1px solid ${colors.cardHeaderBorder}` }}>
        <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: colors.secondaryText }}>
          One Trade, Start to Finish
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: `${s.color}20`, color: s.color }}>
            {s.phase}
          </span>
          <span className="text-[10px]" style={{ color: colors.secondaryText }}>
            {step + 1}/{TRADE_STORY.length}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1" style={{ background: `${s.color}20` }}>
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: s.color }} />
      </div>

      {/* Body */}
      <div key={step} className="p-4 flex flex-col gap-3 de-step-enter" style={{ background: colors.cardBg }}>

        {/* Time + title */}
        <div className="flex items-start gap-3">
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `${s.color}18`, border: `1px solid ${s.color}35` }}>
              <Icon className="w-5 h-5" style={{ color: s.color }} />
            </div>
            <span className="text-[10px] font-black tabular-nums" style={{ color: s.color }}>
              {s.time}
            </span>
          </div>
          <p className="text-[16px] font-black leading-snug pt-1" style={{ color: colors.primaryText }}>
            {s.title}
          </p>
        </div>

        {/* Situation */}
        <div className="rounded-xl p-3" style={{ background: `${colors.border}25` }}>
          <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: colors.secondaryText }}>
            The Situation
          </p>
          <p className="text-[12px] leading-relaxed" style={{ color: colors.primaryText }}>
            {s.situation}
          </p>
        </div>

        {/* Your move */}
        <div className="rounded-xl p-3" style={{ background: `${s.color}0e`, border: `1px solid ${s.color}28` }}>
          <p className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: s.color }}>
            Your Move
          </p>
          <p className="text-[12px] leading-relaxed" style={{ color: colors.primaryText }}>
            {s.move}
          </p>
        </div>

        {/* Lesson */}
        <p className="text-[12px] leading-relaxed italic pl-3"
          style={{ color: colors.secondaryText, borderLeft: `2px solid ${s.color}50` }}>
          {s.lesson}
        </p>

        {/* Nav + CTA */}
        <div className="flex flex-col gap-2 pt-1">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setStep(i => Math.max(0, i - 1))}
              disabled={step === 0}
              className="px-3 py-2 rounded-xl text-[12px] font-bold transition-all"
              style={{
                background: step === 0 ? `${colors.border}30` : `${s.color}18`,
                color: step === 0 ? colors.secondaryText : s.color,
                border: `1px solid ${step === 0 ? colors.border : s.color + '35'}`,
              }}>
              ← Prev
            </button>
            <button onClick={() => navigate(s.path)}
              className="flex-1 py-2 rounded-xl text-[12px] font-bold"
              style={{ background: `linear-gradient(135deg, ${s.color} 0%, ${s.color}cc 100%)`, color: '#fff' }}>
              {s.cta} →
            </button>
            <button
              onClick={() => setStep(i => Math.min(TRADE_STORY.length - 1, i + 1))}
              disabled={step === TRADE_STORY.length - 1}
              className="px-3 py-2 rounded-xl text-[12px] font-bold transition-all"
              style={{
                background: step === TRADE_STORY.length - 1 ? `${colors.border}30` : `${s.color}18`,
                color: step === TRADE_STORY.length - 1 ? colors.secondaryText : s.color,
                border: `1px solid ${step === TRADE_STORY.length - 1 ? colors.border : s.color + '35'}`,
              }}>
              Next →
            </button>
          </div>
          {s.altCta && (
            <button onClick={() => navigate(s.altPath)}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-[12px] font-bold"
              style={{ background: '#6c63ff18', color: '#6c63ff', border: '1px solid #6c63ff30' }}>
              <Sparkles className="w-3.5 h-3.5" /> {s.altCta}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Coach card (Journey tab hero) ───────────────────────────────────────────
function CoachCard({ firstName, currentLevel, lvColor, lvMeta, totalDone, totalTasks, nextTaskId, allDone, colors, navigate }) {
  const pct  = Math.round((totalDone / totalTasks) * 100);
  const meta = TASK_META[nextTaskId];

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ border: `1px solid ${lvColor}35` }}>

      {/* Header */}
      <div className="px-4 py-3"
        style={{ background: `linear-gradient(135deg, ${lvColor}22 0%, ${lvColor}08 100%)` }}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest mb-0.5" style={{ color: lvColor }}>
              {allDone ? 'Journey Complete' : `Level ${currentLevel} · ${lvMeta?.label}`}
            </p>
            <p className="text-[17px] font-black leading-tight" style={{ color: colors.primaryText }}>
              {allDone ? `Amazing, ${firstName}!` : `Hey ${firstName}, here's your next step`}
            </p>
          </div>
          <div className="text-right flex-shrink-0 ml-3">
            <p className="text-[22px] font-black leading-none" style={{ color: lvColor }}>{pct}%</p>
            <p className="text-[10px]" style={{ color: colors.secondaryText }}>{totalDone}/{totalTasks} done</p>
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: `${lvColor}20` }}>
          <div className="h-full rounded-full transition-all duration-700"
            style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${lvColor} 0%, ${lvColor}99 100%)` }} />
        </div>
      </div>

      {/* Next action */}
      {!allDone && meta && (
        <div className="p-4" style={{ background: colors.cardBg }}>
          <p className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: lvColor }}>
            Do this next
          </p>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${lvColor}18`, border: `1px solid ${lvColor}30` }}>
              {React.createElement(meta.icon, { className: 'w-4 h-4', style: { color: lvColor } })}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-bold leading-tight mb-1" style={{ color: colors.primaryText }}>
                {meta.title}
              </p>
              <p className="text-[12px] leading-relaxed" style={{ color: colors.secondaryText }}>
                {meta.why}
              </p>
            </div>
          </div>
          <button onClick={() => navigate(meta.path)}
            className="w-full py-2.5 rounded-xl text-[13px] font-bold flex items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg, ${lvColor} 0%, ${lvColor}cc 100%)`, color: '#fff' }}>
            {meta.cta} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {allDone && (
        <div className="p-4 text-center" style={{ background: colors.cardBg }}>
          <Trophy className="w-7 h-7 mx-auto mb-2" style={{ color: lvColor }} />
          <p className="text-[13px] font-bold" style={{ color: colors.secondaryText }}>
            You've completed all 4 levels. Time to trade with a real edge.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Tab bar ──────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'start', label: 'Journey'   },
  { id: 'map',   label: 'App Guide' },
];

function TabBar({ active, onChange, colors }) {
  return (
    <div className="flex rounded-xl p-1 gap-1"
      style={{ background: `${colors.border}40` }}>
      {TABS.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className="flex-1 py-2 rounded-lg text-[13px] font-bold transition-all"
          style={active === t.id
            ? { background: colors.cardBg, color: colors.primaryText, boxShadow: '0 1px 4px rgba(0,0,0,0.18)' }
            : { color: colors.secondaryText }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function JourneyPage() {
  const { colors }      = useTheme();
  const navigate        = useNavigate();

  return (
    <div className="flex flex-col gap-4 pb-8">
      <OnboardingMap colors={colors} navigate={navigate} />
    </div>
  );
}
